import React from 'react';
import { Store, TrendingUp, Shield, Zap, Globe, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';

const Marketplace = () => {
  const integrations = [
    {
      id: 1,
      name: 'Salesforce Einstein',
      provider: 'Salesforce',
      category: 'CRM',
      description: 'AI-powered CRM insights and automation',
      icon: '☁️',
      rating: 4.8,
      users: '2.5M+',
      price: 'Enterprise',
    },
    {
      id: 2,
      name: 'SAP AI Core',
      provider: 'SAP',
      category: 'ERP',
      description: 'Enterprise resource planning with AI',
      icon: '🏢',
      rating: 4.6,
      users: '1.2M+',
      price: 'Custom',
    },
    {
      id: 3,
      name: 'Oracle AI',
      provider: 'Oracle',
      category: 'Database',
      description: 'Autonomous database with ML capabilities',
      icon: '🗄️',
      rating: 4.7,
      users: '800K+',
      price: 'Enterprise',
    },
    {
      id: 4,
      name: 'Microsoft Copilot',
      provider: 'Microsoft',
      category: 'Productivity',
      description: 'AI assistant for Microsoft 365',
      icon: '💼',
      rating: 4.9,
      users: '5M+',
      price: '$30/mo',
    },
    {
      id: 5,
      name: 'OpenAI GPT-4',
      provider: 'OpenAI',
      category: 'LLM',
      description: 'Advanced language model for text generation',
      icon: '🤖',
      rating: 4.9,
      users: '10M+',
      price: 'Pay-as-you-go',
    },
    {
      id: 6,
      name: 'Claude 4',
      provider: 'Anthropic',
      category: 'LLM',
      description: 'Safe and helpful AI assistant',
      icon: '🧠',
      rating: 4.8,
      users: '3M+',
      price: 'Pay-as-you-go',
    },
    {
      id: 7,
      name: 'Gemini Pro',
      provider: 'Google',
      category: 'LLM',
      description: 'Multimodal AI from Google',
      icon: '✨',
      rating: 4.7,
      users: '4M+',
      price: 'Pay-as-you-go',
    },
    {
      id: 8,
      name: 'Adobe Sensei',
      provider: 'Adobe',
      category: 'Creative',
      description: 'AI for creative workflows',
      icon: '🎨',
      rating: 4.6,
      users: '1.5M+',
      price: 'Included',
    },
  ];

  return (
    <div className="animate-fade-in" data-testid="marketplace-page">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Agent Marketplace
        </h1>
        <p className="text-sm sm:text-base text-gray-400">Discover and integrate agents from top 500 software companies</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="metric-card" data-testid="marketplace-stat-providers">
          <div className="flex items-center gap-3">
            <Store className="w-8 h-8 text-violet-400" />
            <div>
              <p className="text-gray-400 text-sm">Providers</p>
              <h3 className="text-2xl font-bold text-white">500+</h3>
            </div>
          </div>
        </div>
        <div className="metric-card" data-testid="marketplace-stat-agents">
          <div className="flex items-center gap-3">
            <Globe className="w-8 h-8 text-cyan-400" />
            <div>
              <p className="text-gray-400 text-sm">Available Agents</p>
              <h3 className="text-2xl font-bold text-white">2,400+</h3>
            </div>
          </div>
        </div>
        <div className="metric-card" data-testid="marketplace-stat-users">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-green-400" />
            <div>
              <p className="text-gray-400 text-sm">Active Users</p>
              <h3 className="text-2xl font-bold text-white">50M+</h3>
            </div>
          </div>
        </div>
        <div className="metric-card" data-testid="marketplace-stat-rating">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-yellow-400" />
            <div>
              <p className="text-gray-400 text-sm">Avg Rating</p>
              <h3 className="text-2xl font-bold text-white">4.7 ⭐</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Integrations */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-4">Featured Integrations</h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="marketplace-grid">
        {integrations.map((integration) => (
          <div
            key={integration.id}
            className="glass-panel p-6 hover:border-violet-500/50 transition-all cursor-pointer"
            data-testid={`marketplace-item-${integration.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="text-4xl">{integration.icon}</div>
              <div className="px-2 py-1 rounded-lg bg-violet-500/20 text-violet-300 text-xs font-medium">
                {integration.category}
              </div>
            </div>

            <h3 className="text-lg font-semibold text-white mb-2">{integration.name}</h3>
            <p className="text-gray-400 text-sm mb-4">{integration.description}</p>

            <div className="flex items-center gap-4 mb-4 text-sm">
              <div className="flex items-center gap-1">
                <span className="text-yellow-400">★</span>
                <span className="text-white font-medium">{integration.rating}</span>
              </div>
              <div className="text-gray-400">{integration.users} users</div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 text-green-400">
                <DollarSign className="w-4 h-4" />
                <span className="text-sm font-medium">{integration.price}</span>
              </div>
              <Button size="sm" className="btn-primary" data-testid={`integrate-${integration.id}`}>
                <Zap className="w-4 h-4 mr-1" />
                Integrate
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Popular Categories */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold text-white mb-4">Popular Categories</h2>
        <div className="flex flex-wrap gap-3" data-testid="marketplace-categories">
          {['LLM', 'CRM', 'ERP', 'Database', 'Analytics', 'Security', 'Productivity', 'Creative'].map((category) => (
            <button
              key={category}
              className="px-6 py-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-violet-500/50 text-white transition-all"
              data-testid={`category-${category.toLowerCase()}`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Marketplace;